import { useRef, useEffect, useCallback } from 'react';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const CELL_SIZE = 16;
const GRID_COLOR = 'rgba(255,255,255,0.03)';
const ROOM_FILL = '#1a1a2e';
const ROOM_STROKE = '#444';
const ROOM_SELECTED_STROKE = '#4a9eff';
const CORRIDOR_FILL = 'rgba(26,26,46,0.5)';
const ENTITY_COLORS: Record<'monster' | 'npc' | 'object', string> = {
  monster: '#ff6b6b',
  npc: '#4ade80',
  object: '#fbbf24',
};
const TRIGGER_COLOR = '#a855f7';
const ZONE_COLORS = ['#3b82f6', '#f59e0b', '#ef4444'];
const DOOR_COLOR = '#92400e';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface EditorCanvasProps {
  canvasHandlers: {
    onMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onMouseUp: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    onContextMenu: (e: React.MouseEvent) => void;
  };
  attachWheel: (canvas: HTMLCanvasElement | null) => void;
  cursor: string;
  dragPreview: { x: number; y: number; width: number; height: number } | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function EditorCanvas({ canvasHandlers, attachWheel, cursor, dragPreview }: EditorCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const mapData = useDungeonEditorStore((s) => s.mapData);
  const zoom = useDungeonEditorStore((s) => s.zoom);
  const panOffset = useDungeonEditorStore((s) => s.panOffset);
  const selectedRoomId = useDungeonEditorStore((s) => s.selectedRoomId);
  const selectedEntityId = useDungeonEditorStore((s) => s.selectedEntityId);
  const selectedTool = useDungeonEditorStore((s) => s.selectedTool);
  const setZoom = useDungeonEditorStore((s) => s.setZoom);

  // Keep a stable ref of current zoom for zoom controls
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // Attach wheel handler via prop
  useEffect(() => {
    attachWheel(canvasRef.current);
    return () => attachWheel(null);
  }, [attachWheel]);

  // ---------------------------------------------------------------------------
  // Draw
  // ---------------------------------------------------------------------------
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    const w = container.clientWidth;
    const h = container.clientHeight;

    // Resize canvas backing store to match container * dpr
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Background
    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, w, h);

    // Apply pan + zoom
    ctx.save();
    ctx.translate(panOffset.x, panOffset.y);
    ctx.scale(zoom, zoom);

    const cellPx = CELL_SIZE;

    // ---- Grid ---------------------------------------------------------------
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1 / zoom;

    // Only draw visible grid lines
    const startGX = Math.max(0, Math.floor(-panOffset.x / (cellPx * zoom)));
    const startGY = Math.max(0, Math.floor(-panOffset.y / (cellPx * zoom)));
    const endGX = Math.min(mapData.gridWidth, Math.ceil((w - panOffset.x) / (cellPx * zoom)) + 1);
    const endGY = Math.min(mapData.gridHeight, Math.ceil((h - panOffset.y) / (cellPx * zoom)) + 1);

    for (let gx = startGX; gx <= endGX; gx++) {
      ctx.beginPath();
      ctx.moveTo(gx * cellPx, startGY * cellPx);
      ctx.lineTo(gx * cellPx, endGY * cellPx);
      ctx.stroke();
    }
    for (let gy = startGY; gy <= endGY; gy++) {
      ctx.beginPath();
      ctx.moveTo(startGX * cellPx, gy * cellPx);
      ctx.lineTo(endGX * cellPx, gy * cellPx);
      ctx.stroke();
    }

    // ---- Corridors ----------------------------------------------------------
    for (const corridor of mapData.corridors) {
      for (const cell of corridor.cells) {
        ctx.fillStyle = CORRIDOR_FILL;
        ctx.fillRect(cell.x * cellPx, cell.y * cellPx, cellPx, cellPx);
      }

      // Door markers at the first and last cell of the corridor (if has door)
      if (corridor.doorType && corridor.cells.length > 0) {
        const doorCells =
          corridor.cells.length > 1
            ? [corridor.cells[0], corridor.cells[corridor.cells.length - 1]]
            : [corridor.cells[0]];
        for (const dc of doorCells) {
          ctx.fillStyle = DOOR_COLOR;
          const margin = cellPx * 0.2;
          ctx.fillRect(
            dc.x * cellPx + margin,
            dc.y * cellPx + margin,
            cellPx - margin * 2,
            cellPx - margin * 2,
          );
        }
      }
    }

    // ---- Rooms --------------------------------------------------------------
    for (const room of mapData.rooms) {
      const rx = room.x * cellPx;
      const ry = room.y * cellPx;
      const rw = room.width * cellPx;
      const rh = room.height * cellPx;

      // Base fill
      ctx.fillStyle = ROOM_FILL;
      ctx.fillRect(rx, ry, rw, rh);

      // Zone color overlay
      const zoneColor = ZONE_COLORS[room.zone % ZONE_COLORS.length] ?? ZONE_COLORS[0];
      ctx.save();
      ctx.globalAlpha = 0.15;
      ctx.fillStyle = zoneColor;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.restore();

      // Border
      const isSelected = room.id === selectedRoomId;
      ctx.strokeStyle = isSelected ? ROOM_SELECTED_STROKE : ROOM_STROKE;
      ctx.lineWidth = isSelected ? 2 / zoom : 1 / zoom;
      ctx.strokeRect(rx, ry, rw, rh);

      // Room name label above the room
      const fontSize = Math.max(8, Math.min(12, 10 / zoom));
      ctx.font = `${fontSize}px sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(room.name, rx + rw / 2, ry - 2 / zoom, rw);
    }

    // ---- Resize handles (selected room, select tool) -------------------------
    if (selectedRoomId && selectedTool === 'select') {
      const selRoom = mapData.rooms.find((r) => r.id === selectedRoomId);
      if (selRoom) {
        const rx = selRoom.x * cellPx;
        const ry = selRoom.y * cellPx;
        const rw = selRoom.width * cellPx;
        const rh = selRoom.height * cellPx;
        const handleSize = Math.max(3, 5 / zoom);
        const half = handleSize / 2;

        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#4a9eff';
        ctx.lineWidth = 1 / zoom;

        const handles = [
          { x: rx, y: ry },
          { x: rx + rw, y: ry },
          { x: rx, y: ry + rh },
          { x: rx + rw, y: ry + rh },
          { x: rx + rw / 2, y: ry },
          { x: rx + rw / 2, y: ry + rh },
          { x: rx, y: ry + rh / 2 },
          { x: rx + rw, y: ry + rh / 2 },
        ];

        for (const h of handles) {
          ctx.fillRect(h.x - half, h.y - half, handleSize, handleSize);
          ctx.strokeRect(h.x - half, h.y - half, handleSize, handleSize);
        }
      }
    }

    // ---- Entities -----------------------------------------------------------
    for (const entity of mapData.entities) {
      const ex = entity.x * cellPx;
      const ey = entity.y * cellPx;
      const isSelectedEntity = entity.id === selectedEntityId;
      const color = ENTITY_COLORS[entity.type] ?? '#fff';
      const radius = cellPx * 0.35;
      const cx2 = ex + cellPx / 2;
      const cy2 = ey + cellPx / 2;

      if (entity.icon) {
        // Icon emoji
        const fontSize = Math.max(8, cellPx * 0.7);
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(entity.icon, cx2, cy2);
      } else {
        // Colored circle
        ctx.beginPath();
        ctx.arc(cx2, cy2, radius, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }

      // Selection ring
      if (isSelectedEntity) {
        ctx.beginPath();
        ctx.arc(cx2, cy2, radius + 2 / zoom, 0, Math.PI * 2);
        ctx.strokeStyle = ROOM_SELECTED_STROKE;
        ctx.lineWidth = 2 / zoom;
        ctx.stroke();
      }
    }

    // ---- Triggers -----------------------------------------------------------
    for (const trigger of mapData.triggers) {
      if (trigger.targetX === undefined || trigger.targetY === undefined) continue;
      const tx = trigger.targetX * cellPx;
      const ty = trigger.targetY * cellPx;

      // Purple cell overlay
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = TRIGGER_COLOR;
      ctx.fillRect(tx, ty, cellPx, cellPx);
      ctx.restore();

      // Lightning bolt icon
      const fontSize = Math.max(8, cellPx * 0.6);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⚡', tx + cellPx / 2, ty + cellPx / 2);
    }

    // ---- Room drag preview --------------------------------------------------
    if (dragPreview) {
      const px = dragPreview.x * cellPx;
      const py = dragPreview.y * cellPx;
      const pw = dragPreview.width * cellPx;
      const ph = dragPreview.height * cellPx;
      const isValid = dragPreview.width >= 2 && dragPreview.height >= 2;

      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = isValid ? '#4a9eff' : '#ff4444';
      ctx.fillRect(px, py, pw, ph);
      ctx.restore();

      ctx.strokeStyle = isValid ? '#4a9eff' : '#ff4444';
      ctx.lineWidth = 2 / zoom;
      ctx.setLineDash([4 / zoom, 4 / zoom]);
      ctx.strokeRect(px, py, pw, ph);
      ctx.setLineDash([]);
    }

    ctx.restore();
  }, [mapData, zoom, panOffset, selectedRoomId, selectedEntityId, selectedTool, dragPreview]);

  // Redraw when state changes
  useEffect(() => {
    draw();
  }, [draw]);

  // ResizeObserver to redraw on container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      draw();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [draw]);

  // ---------------------------------------------------------------------------
  // Zoom controls
  // ---------------------------------------------------------------------------
  const handleZoomIn = () => setZoom(zoomRef.current + 0.1);
  const handleZoomOut = () => setZoom(zoomRef.current - 0.1);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden"
      style={{ background: '#0d0d1a', cursor }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={canvasHandlers.onMouseDown}
        onMouseMove={canvasHandlers.onMouseMove}
        onMouseUp={canvasHandlers.onMouseUp}
        onContextMenu={canvasHandlers.onContextMenu}
        style={{ display: 'block' }}
      />

      {/* Zoom controls — bottom-right */}
      <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/60 rounded px-2 py-1 select-none">
        <button
          onClick={handleZoomOut}
          className="text-white/70 hover:text-white w-6 h-6 flex items-center justify-center text-lg leading-none"
          title="Zoom out"
        >
          −
        </button>
        <span className="text-white/60 text-xs w-12 text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={handleZoomIn}
          className="text-white/70 hover:text-white w-6 h-6 flex items-center justify-center text-lg leading-none"
          title="Zoom in"
        >
          +
        </button>
      </div>
    </div>
  );
}
